import { AvailabilityTagConfig } from "./types";
import { sdk } from "sdk";

// Configuration cache from backend
let configCache: Record<
    string,
    { purchasable: boolean; notifyStock: boolean }
> | null = null;

// Localization keys for display labels
const DISPLAY_LABEL_KEYS: Record<string, string> = {
    "sold-out": "sold.out",
    "pre-order": "pre.order",
    "low-inventory": "low.inventory",
    "coming-soon": "coming.soon",
};

// Default values for each tag type (fallback when no config is available)
const DEFAULT_VALUES: Record<
    string,
    { purchasable: boolean; notifyStock: boolean }
> = {
    "sold-out": { purchasable: false, notifyStock: true },
    "pre-order": { purchasable: true, notifyStock: false },
    "low-inventory": { purchasable: true, notifyStock: false },
    "coming-soon": { purchasable: false, notifyStock: false },
};

// Fallback configuration for unknown tags
export const DEFAULT_TAG_CONFIG: Omit<
    AvailabilityTagConfig,
    "key" | "displayLabelKey"
> = {
    purchasable: true,
    notifyStock: false,
};

// Function to load configuration from backend
export const loadProductAvailabilityConfig = async (): Promise<void> => {
    try {
        const response = await sdk.callAction({
            actionName: "product-availability/getConfig",
        });

        if (response.isError) {
            console.warn(
                "Failed to load product availability config from backend, using defaults",
            );
            console.warn("Error:", response.error);
        } else {
            configCache = response.data as Record<
                string,
                { purchasable: boolean; notifyStock: boolean }
            >;
        }
    } catch (error) {
        console.error("Error loading product availability config:", error);
    }
};

// Helper function to get tag configuration
export const getTagConfig = (
    key: string,
): Omit<AvailabilityTagConfig, "key"> | null => {
    // Get business logic from cache or fallback to defaults
    const businessConfig = configCache?.[key] || DEFAULT_VALUES[key];

    if (!businessConfig) {
        return null;
    }

    return {
        displayLabelKey: DISPLAY_LABEL_KEYS[key] || key,
        purchasable: businessConfig.purchasable,
        notifyStock: businessConfig.notifyStock,
    };
};
